import { FC, useEffect, useState } from 'react';
import { APPLY_DISCOUNT_CODE_MUTATION } from './graphql';
import { useMutation } from '@apollo/client';
import { Formik, Form, Field, FieldProps, FormikErrors } from 'formik';
import {
  Input,
  InputGroupAddon,
  InputGroup,
  LoadableButton,
  Flex,
  Box,
  Text
} from '../../../components';
import { Valuecode, Warning } from '@urbaninfrastructure/react-icons';
import {
  Label,
  LOADABLE_BUTTON_DEFAULT_SUCCESS_TIMEOUT_MS
} from '@urbaninfrastructure/react-ui-kit';
import {
  applyDiscountCodeMutation,
  applyDiscountCodeMutationVariables,
  applyDiscountCodeMutation_applyDiscountCode_Order,
  applyDiscountCodeMutation_applyDiscountCode_DiscountCodeError
} from '../../../core-types';
import { AnimatePresence, motion } from 'framer-motion';
import { FormattedMessage } from 'react-intl';

type Values = { orderId?: string; code: string };

const FormError: FC<{}> = ({ children }) => {
  return (
    <Flex
      justifyContent="flex-start"
      mb="xxs"
      color="state.error"
      data-testid="Discount__FormError"
    >
      <Warning mr={2} />
      <Text as="span" typoStyle="xs">
        {children}
      </Text>
    </Flex>
  );
};

export const Discount = ({
  orderId,
  onDiscountVerified
}: {
  orderId?: string;
  onDiscountVerified: (
    data: applyDiscountCodeMutation_applyDiscountCode_Order,
    verifiedCode: string
  ) => void;
}) => {
  const [verifiedCode, setVerifiedCode] = useState<string | undefined>();
  const [applyDiscountCode, applyDiscountCodeResult] = useMutation<
    applyDiscountCodeMutation,
    applyDiscountCodeMutationVariables
  >(APPLY_DISCOUNT_CODE_MUTATION);

  let discountError:
    | applyDiscountCodeMutation_applyDiscountCode_DiscountCodeError
    | undefined;

  let discountedOrder:
    | applyDiscountCodeMutation_applyDiscountCode_Order
    | undefined;

  if (
    applyDiscountCodeResult.data &&
    applyDiscountCodeResult.data.applyDiscountCode
  ) {
    if (
      applyDiscountCodeResult.data.applyDiscountCode.__typename ===
      'DiscountCodeError'
    ) {
      discountError = applyDiscountCodeResult.data?.applyDiscountCode;
    } else if (
      applyDiscountCodeResult.data.applyDiscountCode.__typename === 'Order'
    ) {
      discountedOrder = applyDiscountCodeResult.data.applyDiscountCode;
    }
  }

  useEffect(() => {
    const order = discountedOrder;
    if (order && verifiedCode) {
      const timeoutId = setTimeout(() => {
        onDiscountVerified(order, verifiedCode);
      }, LOADABLE_BUTTON_DEFAULT_SUCCESS_TIMEOUT_MS);
      return () => clearTimeout(timeoutId);
    }
  }, [discountedOrder, verifiedCode]);

  return (
    <Formik<Values>
      initialValues={{ orderId, code: '' }}
      enableReinitialize={true}
      validateOnMount={true}
      validate={values => {
        const errors: FormikErrors<Values> = {};
        if (!values.code) {
          errors.code = 'enter_valid_code';
        }
        if (!values.orderId) {
          errors.orderId = 'order_not_provided';
        }
        return errors;
      }}
      onSubmit={(values, form) => {
        if (values.orderId) {
          applyDiscountCode({
            variables: { orderId: values.orderId, code: values.code }
          }).then(result => {
            if (result.data?.applyDiscountCode.__typename === 'Order') {
              setVerifiedCode(values.code);
              setTimeout(() => {
                form.resetForm();
              }, LOADABLE_BUTTON_DEFAULT_SUCCESS_TIMEOUT_MS);
            }
          });
        }
      }}
    >
      {({ isValid }) => {
        return (
          <Form>
            <Field name="code">
              {(fieldProps: FieldProps<Values['code']>) => {
                return (
                  <Box>
                    <Label htmlFor="discount-code">
                      <FormattedMessage
                        id="discount_code"
                        defaultMessage="Discount code"
                      />
                    </Label>
                    <AnimatePresence>
                      {discountError && !applyDiscountCodeResult.loading && (
                        <motion.div
                          key="discount-error"
                          initial={{ y: 50, opacity: 0 }}
                          animate={{ y: 0, opacity: 1 }}
                          exit={{ y: 0, opacity: 0 }}
                        >
                          <FormError>{discountError.message}</FormError>
                        </motion.div>
                      )}
                    </AnimatePresence>

                    <Flex
                      alignItems="center"
                      position="relative"
                      overflow="hidden"
                    >
                      <InputGroup>
                        <InputGroupAddon
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                        >
                          <Valuecode color="primary" />
                        </InputGroupAddon>
                        <Input
                          id="discount-code"
                          alternative={false}
                          {...fieldProps.field}
                        />
                      </InputGroup>
                      <AnimatePresence>
                        {fieldProps.form.values.code && (
                          <Box
                            as={motion.div}
                            key="apply-button"
                            initial={{ x: 100, opacity: 0 }}
                            animate={{ x: 0, opacity: 1 }}
                            exit={{ x: 100, opacity: 0 }}
                            style={{
                              zIndex: 2,
                              position: 'absolute',
                              right: 10
                            }}
                          >
                            <LoadableButton
                              type="submit"
                              disabled={!isValid}
                              loading={applyDiscountCodeResult.loading}
                              responseState={
                                discountError || applyDiscountCodeResult.error
                                  ? 'error'
                                  : 'success'
                              }
                              size="small"
                              shape="cool"
                              variant="primary"
                              ml={2}
                              py={2}
                            >
                              <FormattedMessage
                                id="discount_apply"
                                defaultMessage="Apply"
                              />
                            </LoadableButton>
                          </Box>
                        )}
                      </AnimatePresence>
                    </Flex>
                  </Box>
                );
              }}
            </Field>
          </Form>
        );
      }}
    </Formik>
  );
};
